import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { usePerformer, useUpdatePerformer, useMoveToNotes } from '@/hooks/usePerformers'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { ConfidenceBadge } from '@/components/shared/ConfidenceBadge'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { WikidataLink } from '@/components/shared/WikidataLink'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { Button } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowLeft, Trash2 } from 'lucide-react'
import type { QualityScore } from '@brent/shared/types'

export function PerformerDetail() {
  const { id } = useParams()
  const performerId = Number(id)
  const navigate = useNavigate()
  const { data, isLoading, error } = usePerformer(performerId)
  const { canEdit } = useAuth()
  const updatePerformer = useUpdatePerformer()
  const moveToNotes = useMoveToNotes()
  const { toast } = useToast()
  const [showMoveDialog, setShowMoveDialog] = useState(false)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading performer...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
        <h2 className="font-semibold text-destructive">Failed to load performer</h2>
        <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
      </div>
    )
  }

  if (!data) return null

  const { performer, concerts = [] } = data

  const handleQualityChange = async (qualityScore: QualityScore) => {
    try {
      await updatePerformer.mutateAsync({
        id: performer.id,
        input: { quality_score: qualityScore },
      })
      toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update quality',
      })
    }
  }

  const handleQidSave = async (qid: string) => {
    try {
      const result = await updatePerformer.mutateAsync({
        id: performer.id,
        input: { wikidata_id: qid },
      })
      if (result.enriched) {
        toast({
          title: 'Enriched',
          description: `Matched to ${result.enriched.label}`,
        })
      } else if (result.cleared) {
        toast({ title: 'Cleared', description: 'Wikidata ID removed' })
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update QID',
      })
    }
  }

  const handleMoveToNotes = async () => {
    try {
      const result = await moveToNotes.mutateAsync({ id: performer.id })
      toast({
        title: 'Moved to notes',
        description: `Performer moved to notes on ${result.concerts_updated} concert(s)`,
      })
      setShowMoveDialog(false)
      navigate('/performers')
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to move performer to notes',
      })
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <Link to="/performers">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to performers
          </Button>
        </Link>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{performer.name_canonical}</h1>
            {performer.instrument_or_voice && (
              <p className="mt-1 text-muted-foreground">{performer.instrument_or_voice}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <ConfidenceBadge
              confidence={performer.confidence}
              qualityScore={performer.quality_score}
            />
            {canEdit ? (
              <QualityDropdown
                value={performer.quality_score}
                onChange={handleQualityChange}
                disabled={updatePerformer.isPending}
              />
            ) : (
              <Badge variant="secondary" className="capitalize">
                {performer.quality_score.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Metadata */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Details</h2>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wikidata ID</dt>
            <dd className="mt-1">
              {canEdit ? (
                <InlineEdit
                  value={performer.wikidata_id || ''}
                  onSave={handleQidSave}
                  placeholder="Add QID (e.g. Q1339)"
                />
              ) : (
                <WikidataLink qid={performer.wikidata_id} />
              )}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Wikidata Label</dt>
            <dd className="mt-1 text-sm">{performer.wikidata_label || '-'}</dd>
          </div>
          {performer.instrument_or_voice && (
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Instrument / Voice</dt>
              <dd className="mt-1 text-sm">{performer.instrument_or_voice}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Concert Appearances</dt>
            <dd className="mt-1 text-sm tabular-nums">{performer.appearances}</dd>
          </div>
        </dl>
      </section>

      {/* Move to notes action */}
      {canEdit && (
        <section>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowMoveDialog(true)}
            disabled={moveToNotes.isPending}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Move to concert notes
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Appends this performer's name to the notes field of each concert they appear in, then
            deletes the performer record.
          </p>
        </section>
      )}

      {/* Concert appearances */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">
          Concert Appearances{' '}
          <span className="text-muted-foreground font-normal">({concerts.length})</span>
        </h2>
        {concerts.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead>Ensemble</TableHead>
                  <TableHead>Role</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {concerts.map((concert) => (
                  <TableRow
                    key={concert.concert_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/concerts/${concert.concert_id}`)}
                  >
                    <TableCell className="whitespace-nowrap">
                      {concert.date_text || `Concert #${concert.concert_id}`}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {concert.venue_name || '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {concert.ensemble_name || '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {concert.role || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No concert appearances recorded.</p>
        )}
      </section>

      {/* Move to notes confirmation dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move performer to concert notes?</DialogTitle>
            <DialogDescription>
              This will append "{performer.name_canonical}
              {performer.instrument_or_voice ? ` (${performer.instrument_or_voice})` : ''}" to the
              notes of {concerts.length} concert(s) and permanently delete the performer record.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMoveDialog(false)}
              disabled={moveToNotes.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleMoveToNotes}
              disabled={moveToNotes.isPending}
            >
              {moveToNotes.isPending ? 'Moving...' : 'Move to notes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
